// Tiny triangle soup builder for merged static geometry: flat face normals and per-face
// vertex colours, so a whole island / town / prop field becomes one draw call.

import * as THREE from 'three';

const _c = new THREE.Color();

export function C(hex: number, k = 1): THREE.Color {
  return new THREE.Color(hex).multiplyScalar(k);
}
export function mixC(a: THREE.Color, b: THREE.Color, t: number, out = new THREE.Color()): THREE.Color {
  return out.copy(a).lerp(b, Math.max(0, Math.min(1, t)));
}

/** Deterministic per-call jitter. */
export class Jit {
  s: number;
  constructor(seed: number) { this.s = (seed >>> 0) % 2147483646 + 1; }
  next() { this.s = (this.s * 16807) % 2147483647; return (this.s - 1) / 2147483646; }
  range(a: number, b: number) { return a + (b - a) * this.next(); }
}

export class Geo {
  pos: number[] = [];
  col: number[] = [];
  /** optional per-vertex normals; when every vertex has one, build() keeps them (smooth). */
  nrm: number[] = [];

  get triCount() { return this.pos.length / 9; }

  /** One triangle, counter-clockwise when seen from the front. */
  tri(ax: number, ay: number, az: number, bx: number, by: number, bz: number, cx: number, cy: number, cz: number, c: THREE.Color, k = 1) {
    this.pos.push(ax, ay, az, bx, by, bz, cx, cy, cz);
    const r = c.r * k, g = c.g * k, b = c.b * k;
    this.col.push(r, g, b, r, g, b, r, g, b);
  }

  /** Quad a,b,c,d counter-clockwise from the front. Points as [x,y,z]. */
  quad(a: number[], b: number[], c: number[], d: number[], col: THREE.Color, k = 1) {
    this.tri(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2], col, k);
    this.tri(a[0], a[1], a[2], c[0], c[1], c[2], d[0], d[1], d[2], col, k);
  }

  /** Axis-aligned (optionally yaw-rotated) box centred at cx,cy,cz. sides k: top, side, bottom shade. */
  box(cx: number, cy: number, cz: number, hx: number, hy: number, hz: number, col: THREE.Color, rotY = 0, opts: { top?: boolean; bottom?: boolean; kSide?: number } = {}) {
    const cs = Math.cos(rotY), sn = Math.sin(rotY);
    const p = (x: number, y: number, z: number) => [cx + x * cs + z * sn, cy + y, cz - x * sn + z * cs];
    const v = [
      p(-hx, -hy, -hz), p(hx, -hy, -hz), p(hx, -hy, hz), p(-hx, -hy, hz),
      p(-hx, hy, -hz), p(hx, hy, -hz), p(hx, hy, hz), p(-hx, hy, hz),
    ];
    const ks = opts.kSide ?? 1;
    if (opts.top !== false) this.quad(v[4], v[7], v[6], v[5], col);
    if (opts.bottom) this.quad(v[0], v[1], v[2], v[3], col, 0.8);
    this.quad(v[3], v[2], v[6], v[7], col, ks); // +z
    this.quad(v[1], v[0], v[4], v[5], col, ks); // -z
    this.quad(v[2], v[1], v[5], v[6], col, ks); // +x
    this.quad(v[0], v[3], v[7], v[4], col, ks); // -x
  }

  /**
   * A dressed stone block: top edges chamfered by `c`, slightly irregular, coloured per vertex by
   * `paint(x, y, z, ny, out)` (AO gradient, stains, moss caps), so it reads as worked stone.
   */
  chamferBox(cx: number, cy: number, cz: number, hx: number, hy: number, hz: number, c: number, rotY: number,
    paint: (x: number, y: number, z: number, ny: number, out: THREE.Color) => THREE.Color) {
    const cs = Math.cos(rotY), sn = Math.sin(rotY);
    const p = (x: number, y: number, z: number) => [cx + x * cs + z * sn, cy + y, cz - x * sn + z * cs];
    const yb = -hy, ym = hy - c, yt = hy;
    const B = [p(-hx, yb, -hz), p(hx, yb, -hz), p(hx, yb, hz), p(-hx, yb, hz)];
    const M = [p(-hx, ym, -hz), p(hx, ym, -hz), p(hx, ym, hz), p(-hx, ym, hz)];
    const T = [p(-hx + c, yt, -hz + c), p(hx - c, yt, -hz + c), p(hx - c, yt, hz - c), p(-hx + c, yt, hz - c)];
    const cA = new THREE.Color(), cB = new THREE.Color(), cC = new THREE.Color();
    const tri = (a: number[], b: number[], d: number[], ny: number) => {
      this.pos.push(a[0], a[1], a[2], b[0], b[1], b[2], d[0], d[1], d[2]);
      paint(a[0], a[1], a[2], ny, cA); paint(b[0], b[1], b[2], ny, cB); paint(d[0], d[1], d[2], ny, cC);
      this.col.push(cA.r, cA.g, cA.b, cB.r, cB.g, cB.b, cC.r, cC.g, cC.b);
    };
    const quad = (a: number[], b: number[], d: number[], e: number[], ny: number) => { tri(a, b, d, ny); tri(a, d, e, ny); };
    quad(T[0], T[3], T[2], T[1], 1);
    // sides (+z, -z, +x, -x), then the chamfer above each
    const sidesIdx = [[3, 2], [1, 0], [2, 1], [0, 3]];
    for (const [i0, i1] of sidesIdx) {
      quad(B[i0], B[i1], M[i1], M[i0], 0);
      quad(M[i0], M[i1], T[i1], T[i0], 0.7);
    }
  }

  /** Box between two points (beams, wires with thickness, brackets). */
  beam(a: THREE.Vector3, b: THREE.Vector3, w: number, h: number, col: THREE.Color) {
    const d = _v1.subVectors(b, a);
    const len = d.length();
    _m.lookAt(a, b, _up.set(0, 1, 0).cross(d).lengthSq() < 1e-6 ? _alt : _up.set(0, 1, 0));
    _m.setPosition(_v2.addVectors(a, b).multiplyScalar(0.5));
    const g = new THREE.BoxGeometry(w, h, len).toNonIndexed();
    this.addGeometry(g, _m, col);
  }

  /** Vertical prism with per-vertex radii (faceted rocks, poles, drums). */
  prism(cx: number, cz: number, y0: number, y1: number, rBot: number[], rTop: number[], a0: number, col: THREE.Color, opts: { top?: boolean; kTop?: number; dx?: number; dz?: number } = {}) {
    const n = rBot.length;
    const dx = opts.dx ?? 0, dz = opts.dz ?? 0;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const ai = a0 + (i / n) * Math.PI * 2, aj = a0 + (j / n) * Math.PI * 2;
      const ci = Math.cos(ai), si = Math.sin(ai), cj = Math.cos(aj), sj = Math.sin(aj);
      this.quad(
        [cx + ci * rBot[i], y0, cz + si * rBot[i]],
        [cx + ci * rTop[i] + dx, y1, cz + si * rTop[i] + dz],
        [cx + cj * rTop[j] + dx, y1, cz + sj * rTop[j] + dz],
        [cx + cj * rBot[j], y0, cz + sj * rBot[j]],
        col,
      );
      if (opts.top !== false) {
        this.tri(cx + dx, y1, cz + dz, cx + cj * rTop[j] + dx, y1, cz + sj * rTop[j] + dz, cx + ci * rTop[i] + dx, y1, cz + si * rTop[i] + dz, col, opts.kTop ?? 1.06);
      }
    }
  }

  /** Append another (any) geometry transformed by m with a solid colour (optionally jittered per face). */
  addGeometry(g: THREE.BufferGeometry, m: THREE.Matrix4 | null, col: THREE.Color, jitter = 0, seed = 1) {
    const ng = g.index ? g.toNonIndexed() : g;
    const p = ng.attributes.position;
    const j = jitter ? new Jit(seed) : null;
    for (let i = 0; i < p.count; i += 3) {
      const k = j ? 1 + (j.next() - 0.5) * jitter : 1;
      for (let t = 0; t < 3; t++) {
        _v1.fromBufferAttribute(p, i + t);
        if (m) _v1.applyMatrix4(m);
        this.pos.push(_v1.x, _v1.y, _v1.z);
        this.col.push(col.r * k, col.g * k, col.b * k);
      }
    }
  }

  /** Triangle with per-vertex colours and normals (smooth, painted shading). */
  triS(a: number[], b: number[], c: number[], na: number[], nb: number[], nc: number[], ca: THREE.Color, cb: THREE.Color, cc: THREE.Color) {
    this.pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
    this.nrm.push(na[0], na[1], na[2], nb[0], nb[1], nb[2], nc[0], nc[1], nc[2]);
    this.col.push(ca.r, ca.g, ca.b, cb.r, cb.g, cb.b, cc.r, cc.g, cc.b);
  }

  append(o: Geo) {
    for (const v of o.pos) this.pos.push(v);
    for (const v of o.col) this.col.push(v);
  }

  build(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    if (this.nrm.length === this.pos.length) g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nrm, 3));
    else g.computeVertexNormals(); // non-indexed: face normals = flat facets
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }
}

const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _up = new THREE.Vector3();
const _alt = new THREE.Vector3(1, 0, 0);
const _m = new THREE.Matrix4();
export { _c as scratchColor };

/**
 * Give a flat-faceted triangle soup smooth, welded vertex normals: each corner averages the
 * (area-weighted) normals of the triangles sharing its position whose facing is within the
 * crease angle of its own (cos >= `cosCrease`), so rock reads as rounded mass while a real edge
 * (a cap rim, a plate lip) stays crisp. Leaves a Geo that already carries normals alone.
 */
export function weldNormals(g: Geo, cosCrease = 0.5) {
  const P = g.pos, nT = P.length / 9;
  if (g.nrm.length === P.length || nT === 0) return;
  const fa = new Float32Array(nT * 3), fu = new Float32Array(nT * 3);
  for (let t = 0; t < nT; t++) {
    const o = t * 9;
    const ux = P[o + 3] - P[o], uy = P[o + 4] - P[o + 1], uz = P[o + 5] - P[o + 2];
    const vx = P[o + 6] - P[o], vy = P[o + 7] - P[o + 1], vz = P[o + 8] - P[o + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const l = Math.hypot(nx, ny, nz) || 1;
    fa[t * 3] = nx; fa[t * 3 + 1] = ny; fa[t * 3 + 2] = nz;
    fu[t * 3] = nx / l; fu[t * 3 + 1] = ny / l; fu[t * 3 + 2] = nz / l;
  }
  // corners grouped by quantised position (numeric hash; a rare collision only merges a crease test)
  const groups = new Map<number, number[]>();
  const keys = new Float64Array(nT * 3);
  for (let v = 0; v < nT * 3; v++) {
    const k = (Math.round(P[v * 3] * 200) * 73856093) ^ (Math.round(P[v * 3 + 1] * 200) * 19349663) ^ (Math.round(P[v * 3 + 2] * 200) * 83492791);
    keys[v] = k;
    const l = groups.get(k);
    if (l) l.push(v / 3 | 0); else groups.set(k, [v / 3 | 0]);
  }
  const N = new Array<number>(P.length);
  for (let v = 0; v < nT * 3; v++) {
    const t = v / 3 | 0, list = groups.get(keys[v])!;
    let x = 0, y = 0, z = 0;
    for (const s of list) {
      if (fu[t * 3] * fu[s * 3] + fu[t * 3 + 1] * fu[s * 3 + 1] + fu[t * 3 + 2] * fu[s * 3 + 2] < cosCrease) continue;
      x += fa[s * 3]; y += fa[s * 3 + 1]; z += fa[s * 3 + 2];
    }
    const l = Math.hypot(x, y, z) || 1;
    N[v * 3] = x / l; N[v * 3 + 1] = y / l; N[v * 3 + 2] = z / l;
  }
  g.nrm = N;
}
